interface SpeechConfig {
  subscriptionKey: string;
  region: string;
  voiceName?: string;
  language?: string;
}

interface SpeechSynthesisRequest {
  text: string;
  voice?: string;
  rate?: string;
  pitch?: string;
}

interface StreamingSpeechSynthesisRequest {
  text: string;
  voice?: string;
  rate?: string;
  pitch?: string;
  onChunkComplete?: () => void;
}

interface SpeechRecognitionResult {
  text: string;
  confidence: number;
}

class AzureSpeechService {
  private config: SpeechConfig;
  private abortController: AbortController | null = null;
  private audioContext: AudioContext | null = null;
  private audioSource: AudioBufferSourceNode | null = null;
  private speechRecognition: any = null;

  constructor(config: SpeechConfig) {
    this.config = {
      voiceName: 'pl-PL-AgnieszkaNeural',
      language: 'pl-PL',
      ...config
    };
  }

  private getEndpoint(): string {
    return `https://${this.config.region}.tts.speech.microsoft.com/cognitiveservices/v1`;
  }



  private async getAccessToken(): Promise<string> {
    const tokenEndpoint = `https://${this.config.region}.api.cognitive.microsoft.com/sts/v1.0/issueToken`;

    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Ocp-Apim-Subscription-Key': this.config.subscriptionKey,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: 'grant_type=client_credentials',
    });

    if (!response.ok) {
      throw new Error(`Failed to get access token: ${response.statusText}`);
    }

    return await response.text();
  }

  private createSSML(text: string, voice: string = this.config.voiceName!): string {
    // Clean the text for speech synthesis
    const cleanText = text
      .replace(/```[\s\S]*?```/g, '') // Remove code blocks
      .replace(/`[^`]*`/g, '') // Remove inline code
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // Convert markdown links to text
      .replace(/\*\*([^*]*)\*\*/g, '$1') // Remove bold formatting
      .replace(/\*([^*]*)\*/g, '$1') // Remove italic formatting
      .replace(/#{1,6}\s+/g, '') // Remove headers
      .replace(/\n+/g, ' ') // Replace multiple newlines with single space
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();

    if (cleanText.length === 0) {
      throw new Error('No text content to synthesize');
    }

    return `
      <speak version='1.0' xml:lang='${this.config.language}'>
        <voice xml:lang='${this.config.language}' xml:gender='Female' name='${voice}'>
          ${cleanText.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}
        </voice>
      </speak>
    `;
  }

  async synthesizeSpeech(request: SpeechSynthesisRequest): Promise<void> {
    // Stop any ongoing speech first
    await this.stopSpeech();

    try {
      const accessToken = await this.getAccessToken();
      const ssml = this.createSSML(request.text, request.voice);

      // Create new abort controller for this request
      this.abortController = new AbortController();

      const response = await fetch(this.getEndpoint(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/ssml+xml',
          'X-Microsoft-OutputFormat': 'audio-16khz-128kbitrate-mono-mp3',
          'User-Agent': 'CVPortal-React-App',
        },
        body: ssml,
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Speech synthesis failed: ${response.statusText}`);
      }

      const audioBuffer = await response.arrayBuffer();
      await this.playAudio(audioBuffer);

    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Speech synthesis was cancelled');
      } else {
        console.error('Speech synthesis error:', error);
        throw error;
      }
    }
  }

  async synthesizeSpeechStreaming(request: StreamingSpeechSynthesisRequest): Promise<void> {
    // Stop any ongoing speech first
    await this.stopSpeech();

    try {
      // Split text into smaller chunks for faster processing
      const chunks = this.splitIntoChunks(request.text, 100); // 100 characters per chunk
      const accessToken = await this.getAccessToken();

      // Initialize audio context for streaming
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      }

      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // Pre-fetch all audio chunks in parallel
      const audioChunks = await this.preFetchAudioChunks(chunks, accessToken, request.voice);

      // Play chunks sequentially with pre-fetched audio
      for (let i = 0; i < audioChunks.length; i++) {
        const audioBuffer = audioChunks[i];
        if (!audioBuffer) continue;

        try {
          // Play this chunk and wait for it to complete
          await this.playAudioChunkSequential(audioBuffer);

          // Call callback when chunk is complete
          if (request.onChunkComplete) {
            request.onChunkComplete();
          }

        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            console.log('Speech synthesis was cancelled');
            break;
          } else {
            console.error('Error playing chunk:', error);
            // Continue with next chunk even if one fails
          }
        }
      }

    } catch (error) {
      console.error('Streaming speech synthesis error:', error);
      throw error;
    }
  }

  private async preFetchAudioChunks(chunks: string[], accessToken: string, voice?: string): Promise<(ArrayBuffer | null)[]> {
    const audioChunks: (ArrayBuffer | null)[] = [];

    // Pre-fetch all chunks in parallel
    const fetchPromises = chunks.map(async (chunk, index) => {
      if (chunk.trim().length === 0) {
        return null;
      }

      try {
        const ssml = this.createSSML(chunk, voice);

        const response = await fetch(this.getEndpoint(), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/ssml+xml',
            'X-Microsoft-OutputFormat': 'audio-16khz-128kbitrate-mono-mp3',
            'User-Agent': 'CVPortal-React-App',
          },
          body: ssml,
        });

        if (!response.ok) {
          throw new Error(`Speech synthesis failed: ${response.statusText}`);
        }

        return await response.arrayBuffer();
      } catch (error) {
        console.error(`Error pre-fetching chunk ${index}:`, error);
        return null;
      }
    });

    // Wait for all chunks to be fetched
    const results = await Promise.all(fetchPromises);
    return results;
  }

  private splitIntoChunks(text: string, chunkSize: number): string[] {
    const words = text.split(' ');
    const chunks: string[] = [];
    let currentChunk = '';

    for (const word of words) {
      if ((currentChunk + ' ' + word).length > chunkSize && currentChunk.length > 0) {
        chunks.push(currentChunk.trim());
        currentChunk = word;
      } else {
        currentChunk += (currentChunk ? ' ' : '') + word;
      }
    }

    if (currentChunk.trim()) {
      chunks.push(currentChunk.trim());
    }

    return chunks;
  }

  private async playAudioChunk(audioBuffer: ArrayBuffer): Promise<void> {
    try {
      // Decode the audio data
      const decodedAudio = await this.audioContext!.decodeAudioData(audioBuffer);

      // Create and configure audio source
      const audioSource = this.audioContext!.createBufferSource();
      audioSource.buffer = decodedAudio;
      audioSource.connect(this.audioContext!.destination);

      // Start playback immediately
      audioSource.start(0);

      // Clean up when done
      audioSource.onended = () => {
        // Audio source will be garbage collected
      };

    } catch (error) {
      console.error('Error playing audio chunk:', error);
      throw error;
    }
  }

  private async playAudioChunkSequential(audioBuffer: ArrayBuffer): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Decode the audio data
        this.audioContext!.decodeAudioData(audioBuffer)
          .then(decodedAudio => {
            // Create and configure audio source
            const audioSource = this.audioContext!.createBufferSource();
            audioSource.buffer = decodedAudio;
            audioSource.connect(this.audioContext!.destination);

            // Set up completion handler
            audioSource.onended = () => {
              resolve();
            };

            // Start playback
            audioSource.start(0);

          })
          .catch(error => {
            console.error('Error decoding audio chunk:', error);
            reject(error);
          });

      } catch (error) {
        console.error('Error playing audio chunk:', error);
        reject(error);
      }
    });
  }

  private createStreamingSSML(text: string, voice: string = this.config.voiceName!): string {
    // Clean the text for speech synthesis
    const cleanText = text
      .replace(/```[\s\S]*?```/g, '') // Remove code blocks
      .replace(/`[^`]*`/g, '') // Remove inline code
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // Convert markdown links to text
      .replace(/\*\*([^*]*)\*\*/g, '$1') // Remove bold formatting
      .replace(/\*([^*]*)\*/g, '$1') // Remove italic formatting
      .replace(/#{1,6}\s+/g, '') // Remove headers
      .replace(/\n+/g, ' ') // Replace multiple newlines with single space
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();

    if (cleanText.length === 0) {
      throw new Error('No text content to synthesize');
    }

    // Add SSML optimizations for faster processing
    const optimizedText = cleanText
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/([.!?])\s+/g, '$1<break time="200ms"/>') // Add small breaks between sentences
      .replace(/([,;:])\s+/g, '$1<break time="100ms"/>'); // Add tiny breaks for punctuation

    return `
      <speak version='1.0' xml:lang='${this.config.language}'>
        <voice xml:lang='${this.config.language}' xml:gender='Female' name='${voice}'>
          <prosody rate="1.1">${optimizedText}</prosody>
        </voice>
      </speak>
    `;
  }

  private splitIntoSentences(text: string): string[] {
    // Split by sentence endings, but be smart about abbreviations
    const sentences = text
      .replace(/([.!?])\s*(?=[A-Z])/g, '$1|') // Split on sentence endings followed by capital letters
      .split('|')
      .map(s => s.trim())
      .filter(s => s.length > 0);

    // If no sentences found, split by paragraphs
    if (sentences.length <= 1) {
      return text
        .split('\n\n')
        .map(s => s.trim())
        .filter(s => s.length > 0);
    }

    return sentences;
  }

  async startSpeechRecognition(
    onResult: (result: SpeechRecognitionResult) => void,
    onError: (error: string) => void,
    onEnd: () => void
  ): Promise<void> {
    try {
      // Check if Web Speech API is available
      if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
        throw new Error('Speech recognition is not supported in this browser');
      }

      // Create speech recognition instance
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      this.speechRecognition = new SpeechRecognition();

      // Configure speech recognition
      this.speechRecognition.continuous = false;
      this.speechRecognition.interimResults = false;
      this.speechRecognition.lang = this.config.language;
      this.speechRecognition.maxAlternatives = 1;

      // Set up event handlers
      this.speechRecognition.onresult = (event: any) => {
        const result = event.results[0];
        if (result.isFinal) {
          const recognizedText = result[0].transcript;
          const confidence = result[0].confidence;

          onResult({
            text: recognizedText,
            confidence: confidence
          });
        }
      };

      this.speechRecognition.onerror = (event: any) => {
        let errorMessage = 'Speech recognition failed';

        switch (event.error) {
          case 'no-speech':
            errorMessage = 'No speech detected';
            break;
          case 'audio-capture':
            errorMessage = 'Microphone access denied';
            break;
          case 'not-allowed':
            errorMessage = 'Microphone permission denied';
            break;
          case 'network':
            errorMessage = 'Network error during recognition';
            break;
          default:
            errorMessage = `Recognition error: ${event.error}`;
        }

        onError(errorMessage);
      };

      this.speechRecognition.onend = () => {
        onEnd();
      };

      // Start recognition
      this.speechRecognition.start();

    } catch (error) {
      onError(error instanceof Error ? error.message : 'Failed to start speech recognition');
    }
  }

  async stopSpeechRecognition(): Promise<void> {
    if (this.speechRecognition) {
      try {
        this.speechRecognition.stop();
      } catch (error) {
        console.error('Error stopping speech recognition:', error);
      }
      this.speechRecognition = null;
    }
  }

  private async playAudio(audioBuffer: ArrayBuffer): Promise<void> {
    try {
      // Initialize AudioContext if not already done
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      }

      // Resume AudioContext if suspended
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // Decode the audio data
      const decodedAudio = await this.audioContext.decodeAudioData(audioBuffer);

      // Create and configure audio source
      this.audioSource = this.audioContext.createBufferSource();
      this.audioSource.buffer = decodedAudio;
      this.audioSource.connect(this.audioContext.destination);

      // Set up event handlers
      this.audioSource.onended = () => {
        this.audioSource = null;
      };

      // Note: onerror is deprecated, but we'll handle errors in the catch block

      // Start playback
      this.audioSource.start(0);

    } catch (error) {
      console.error('Error playing audio:', error);
      throw error;
    }
  }

  async stopSpeech(): Promise<void> {
    // Abort any ongoing HTTP request
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    // Stop audio playback
    if (this.audioSource) {
      try {
        this.audioSource.stop();
        this.audioSource.disconnect();
      } catch (error) {
        // Ignore errors when stopping already stopped audio
      }
      this.audioSource = null;
    }

    // Close AudioContext if it exists
    if (this.audioContext) {
      try {
        await this.audioContext.close();
      } catch (error) {
        // Ignore errors when closing AudioContext
      }
      this.audioContext = null;
    }
  }

  isSpeaking(): boolean {
    return this.audioSource !== null;
  }

  isRecording(): boolean {
    return this.speechRecognition !== null;
  }

  updateLanguage(language: string, voiceName: string): void {
    this.config.language = language;
    this.config.voiceName = voiceName;
  }

  dispose(): void {
    this.stopSpeech();
    this.stopSpeechRecognition();
  }
}

// Singleton instance
let speechServiceInstance: AzureSpeechService | null = null;
let hasWarnedAboutCredentials = false;

export const getAzureSpeechService = (): AzureSpeechService | null => {
  const subscriptionKey = import.meta.env.REACT_APP_SPEECH_SUBSCRIPTION_KEY;
  const region = import.meta.env.REACT_APP_SPEECH_REGION;

  if (!subscriptionKey || !region) {
    // Only warn once to avoid console spam
    if (!hasWarnedAboutCredentials) {
      console.warn('Azure Speech Service credentials not configured');
      hasWarnedAboutCredentials = true;
    }
    return null;
  }

  // Get current language from localStorage (same as translation service)
  const currentLanguage = localStorage.getItem('language') || 'en';

  // Map language codes to Azure Speech Service language codes and voices
  const languageConfig = getLanguageConfig(currentLanguage);

  if (!speechServiceInstance) {
    speechServiceInstance = new AzureSpeechService({
      subscriptionKey,
      region,
      voiceName: languageConfig.voiceName,
      language: languageConfig.language
    });
  } else {
    // Update existing instance with new language settings
    speechServiceInstance.updateLanguage(languageConfig.language, languageConfig.voiceName);
  }

  return speechServiceInstance;
};

const getLanguageConfig = (languageCode: string) => {
  switch (languageCode) {
    case 'en':
      return { language: 'en-US', voiceName: 'en-US-JennyNeural' };
    case 'fr':
      return { language: 'fr-FR', voiceName: 'fr-FR-DeniseNeural' };
    case 'de':
      return { language: 'de-DE', voiceName: 'de-DE-KatjaNeural' };
    case 'es':
      return { language: 'es-ES', voiceName: 'es-ES-ElviraNeural' };
    case 'no':
      return { language: 'nb-NO', voiceName: 'nb-NO-IselinNeural' };
    case 'pl':
      return { language: 'pl-PL', voiceName: 'pl-PL-AgnieszkaNeural' };
    default:
      return { language: 'en-US', voiceName: 'en-US-JennyNeural' };
  }
};

export default AzureSpeechService; 
